import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Eye, EyeClosed } from '@phosphor-icons/react';
import { Oval } from 'react-loader-spinner';
import { useTheme } from '../../providers/theme';
import { Button } from '../../components/Button';
import './Login.styles.css';
import { useTranslation } from 'react-i18next';
import { LanguageSwitch } from '../../components/LanguageSwitch';

type LoginProps = {
  loadingButton: boolean;
  signIn: (email: string, password: string) => void;
}

export function Login({ loadingButton, signIn }: LoginProps) {
  const [visible, setVisible] = useState(false);
  const { theme } = useTheme();
  const esmartLinkWeb = 'https://esmartbr.com.br';
  const backgroundLoginScreen = require('../../assets/images/backgroundLoginScreen.png');
  const imtraffLogo = require('../../assets/images/imtraffLogo.png');
  const esmartLogo = require('../../assets/images/esmartLogoWhite.png');
  const handlerLogo = require('../../assets/images/logoHandler.png');

  const { t } = useTranslation();

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email('E-mail inválido!').required('Por favor, preencher o campo!'),
      password: Yup.string().required('Por favor, preencher o campo!')
    }),
    validateOnMount: true,
    validateOnChange: true,
    onSubmit: values => {
      signIn(values.email, values.password);
    },

  });

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100vw', height: '100vh', flexDirection: 'row' }}>
        <section style={{ flex: 1, height: '100vh', overflow: 'hidden' }}>
          <div
            style={{
              display: 'flex',
              height: '100vh',
              width: '100%',
              flex: 1,
              backgroundImage: `url(${backgroundLoginScreen})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 30px',
            }}
          >
            <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', paddingLeft: '6rem' }}>
              <span style={{ fontSize: '50px', fontWeight: 'bold', color: '#fff' }}>
                {t('login-header')}
              </span>

              <span style={{ fontSize: '18px', color: '#fff' }}>
                {t('login-cta')}
              </span>

              <span style={{ marginTop: '40px', fontSize: '18px', color: '#fff' }}>
                {t('login-question')}
              </span>

              <span style={{ marginBottom: '10px', fontSize: '18px', color: '#fff' }}>
                {t('login-plans-cta')}
              </span>

              <Button
                id="login-see-plans"
                type='button'
                onClick={() => window.open(esmartLinkWeb)}
                color={theme.colors.main}
                sxhover={theme.colors.hoverMain}
                sxwidth="210px"
              >
                {t('login-button-plans')}
              </Button>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 30, marginTop: '40px'}}>
                <img src={imtraffLogo} alt="Imtraff Logo" style={{ width: '273px', height: '85px' }} />
                <img src={esmartLogo} alt="Esmart Logo" style={{ width: '273px', height: '85px' }} />
              </div>
            </div>
          </div>
        </section>

        <section style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div style={{ backgroundColor: '#EAEAEA', borderRadius: 8, width: '380px', height: 'auto', boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px', }}>
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '10px' }}>
              <img src={handlerLogo} style={{ width: 210, marginTop: 10 }} alt="Handler Logo" />
            </div>

            <form onSubmit={formik.handleSubmit}>
              <div style={{ padding: '0 20px', width: '100%', display: 'flex', flexDirection: 'column' }}>
                <input
                  id='email'
                  name='email'
                  type="text"
                  onChange={formik.handleChange}
                  value={formik.values.email}
                  className='inputEmailForm'
                  placeholder={t('login-email')}
                />

                {
                  formik.errors.email && formik.touched.email ? (
                    <div>
                      <span style={{ fontSize: '12px' }}>{formik.errors.email}</span>
                    </div>
                  ) : null
                }

                <div className='inputPasswordForm'>
                  <input
                    id='password'
                    name='password'
                    type={visible ? 'text' : 'password'}
                    onChange={formik.handleChange}
                    value={formik.values.password}
                    placeholder={t('login-password')}
                    style={{ width: '100%', height: '100%', backgroundColor: '#F3F3F3', outline: 'none', fontWeight: 'bold', fontSize: '12px' }}
                  />
                  <div style={{ paddingRight: 10 }}>
                    {visible
                      ? <EyeClosed size={25} cursor='pointer' onClick={() => setVisible(prevState => !prevState)} />
                      : <Eye size={25} cursor='pointer' onClick={() => setVisible(prevState => !prevState)} />}
                  </div>
                </div>
                {formik.touched.password && formik.errors.password ? (
                  <span style={{ fontSize: '12px' }}>{formik.errors.password}</span>
                ) : null
                }

                <span style={sxForgotYourPassword} onClick={() => alert(t('forgot-password-warning'))}>{t('login-button-forgot-password')}</span>

                <Button id="login-submit" type='submit' color={theme.colors.main} sxhover={theme.colors.hoverMain} sxwidth='340px'>
                  {
                    loadingButton ? (
                      <Oval
                        height={20}
                        width={20}
                        color="#FFF"
                        visible={true}
                        ariaLabel='oval-loading'
                        secondaryColor={theme.colors.hoverMain}
                        strokeWidth={5}
                        strokeWidthSecondary={5}
                      />
                    ) : t('login-button-access')
                  }
                </Button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '10px 0px' }}>
                <LanguageSwitch />
              </div>
            </form>
          </div>
        </section>
      </div>
    </>
  );
}

const sxForgotYourPassword = {
  fontSize: '12px',
  fontWeight: 'bold',
  color: '#7E7E7E',
  marginTop: '30px',
  marginBottom: '10px',
  cursor: 'pointer'
};

